import { Color, NormalBlending, ShaderMaterial } from 'three';

import vertexShader from './pinMaterialVertex.glsl';
import fragmentShader from './pinMaterialFragment.glsl';
import AppEngine from '../general/appEngine';

export default class PinMaterial extends ShaderMaterial {
  static MAX_EXCLUDED_IDS = 8;

  constructor(instanced, allowExclusion) {
    const uniforms = {
      pinSize: {
        value: 0.008,
      },
      baseScale: {
        value: 1.0,
      },
      highlightScale: {
        value: 1.5,
      },
      numPins: {
        value: 1.0,
      },
      exposure: {
        value: 2.0 * AppEngine.HDR_SCALE,
      },
      validFactor: {
        value: 1.0,
      },
      invalidColor: {
        value: new Color(0xff30ff),
      },
      sphereDistance: {
        value: 5.3,
      },
      pinToSphere: {
        value: 1.0,
      },
      phase: {
        value: 0.0,
      },
      excludeIDs: {
        value: new Uint32Array([-1, -1, -1, -1, -1, -1, -1, -1]),
      },
      highlightPinID: {
        value: -1,
      },
      rippleMap: {
        value: null,
      },
      appearPhase: {
        value: 0.0,
      },
      // only if instanced === false
      pinColor: {
        value: new Color(1, 1, 1),
      },
    };
    super({
      uniforms,
      vertexShader,
      fragmentShader,
      transparent: true,
      blending: NormalBlending,
      depthWrite: false,
      vertexColors: true,
      extensions: {
        derivatives: true,
      },
      defines: {
        INSTANCED: instanced ? 1 : 0,
        ALLOW_EXCLUSION: allowExclusion ? 1 : 0,
        MAX_EXCLUDED_IDS: PinMaterial.MAX_EXCLUDED_IDS,
      },
    });
  }

  get exposure() {
    return this.uniforms.exposure.value / AppEngine.HDR_SCALE;
  }

  set exposure(value) {
    this.uniforms.exposure.value = value * AppEngine.HDR_SCALE;
  }

  get numPins() {
    return this.uniforms.numPins.value;
  }

  set numPins(value) {
    this.uniforms.numPins.value = value;
  }

  get phase() {
    return this.uniforms.phase.value;
  }

  set phase(value) {
    this.uniforms.phase.value = value;
  }

  get pinToSphere() {
    return this.uniforms.pinToSphere.value;
  }

  set pinToSphere(value) {
    this.uniforms.pinToSphere.value = value;
  }

  get rippleMap() {
    return this.uniforms.rippleMap.value;
  }

  set rippleMap(value) {
    this.uniforms.rippleMap.value = value;
  }

  get highlightPinID() {
    return this.uniforms.highlightPinID.value;
  }

  set highlightPinID(value) {
    this.uniforms.highlightPinID.value = value;
  }

  get appearPhase() {
    return this.uniforms.appearPhase.value;
  }

  set appearPhase(value) {
    this.uniforms.appearPhase.value = value;
  }

  setExcludedIDs(value) {
    if (value) {
      const len = Math.min(value.length, PinMaterial.MAX_EXCLUDED_IDS);
      const arr = this.uniforms.excludeIDs.value;
      for (let i = 0; i < len; ++i) {
        arr[i] = value[i];
      }
      for (let i = len; i < PinMaterial.MAX_EXCLUDED_IDS; ++i) {
        arr[i] = -1;
      }
      this.uniforms.excludeIDs.value = arr;
    }
  }

  get pinSize() {
    return this.uniforms.pinSize.value;
  }

  set pinSize(value) {
    this.uniforms.pinSize.value = value;
  }

  get baseScale() {
    return this.uniforms.baseScale.value;
  }

  set baseScale(value) {
    this.uniforms.baseScale.value = value;
  }

  get validFactor() {
    return this.uniforms.validFactor.value;
  }

  set validFactor(value) {
    this.uniforms.validFactor.value = value;
  }

  get pinColor() {
    return this.uniforms.pinColor.value;
  }

  set pinColor(value) {
    this.uniforms.pinColor.value = value;
  }
}
